import { useMemo } from 'react';

import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { improvementsEndpoints as endpoints } from '@/api/endpoints';
import { callGet, callPatch } from '@/api/fetcher';

import type { Improvement, ImprovementsListResponse, UpdateImprovementBody } from '../types';
import { countPending, sortImprovements } from '../utils';

export const API = Object.freeze({
  listImprovements: async (
    brainId: string,
    params?: { cursor?: string }
  ): Promise<ImprovementsListResponse> =>
    callGet(endpoints.improvements(brainId, { cursor: params?.cursor })),

  updateImprovement: async (
    brainId: string,
    id: string,
    body: UpdateImprovementBody
  ): Promise<Improvement> => callPatch(endpoints.improvement(brainId, id), body),
});

/**
 * Loads the brain's improvements inbox. Fetched unfiltered (status filtering is
 * client-side) so a single cache entry backs the list, the pending nav badge,
 * and the waiting-vs-caught-up zero-state distinction.
 */
export const useImprovements = (brainId?: string) => {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [endpoints.improvements(brainId!)],
      queryFn: ({ pageParam }) => API.listImprovements(brainId!, { cursor: pageParam?.cursor }),
      getNextPageParam: (lastPage: ImprovementsListResponse) =>
        lastPage.pagination?.has_more && lastPage.pagination?.next_cursor
          ? { cursor: lastPage.pagination.next_cursor }
          : undefined,
      initialPageParam: {} as { cursor?: string },
      enabled: !!brainId,
      placeholderData: keepPreviousData,
    });

  const improvements = useMemo(
    () => sortImprovements(data?.pages.flatMap((page) => page.improvements) ?? []),
    [data]
  );
  const pendingCount = useMemo(() => countPending(improvements), [improvements]);

  return {
    improvements,
    pendingCount,
    hasAny: improvements.length > 0,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

/**
 * Pending count for the agent nav dropdown (D8).
 */
export const usePendingImprovementsCount = (brainId?: string) => {
  const { pendingCount } = useImprovements(brainId);
  return pendingCount;
};
